import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheckIcon, CreditCardIcon, UsersIcon, WebhookIcon, DatabaseIcon, ArrowRightIcon, AlertTriangleIcon, RepeatIcon, ReceiptIcon, CalculatorIcon, TicketIcon, LinkIcon, CodeIcon } from 'lucide-react';
import { CodeBlock } from '../components/ui/CodeBlock';
import { getWizardState, getRecommendation } from '../lib/wizardUtils';
import type { StripeModule } from '../lib/wizardUtils';
const ICON_MAP: Record<string, React.ElementType> = {
  CreditCard: CreditCardIcon,
  Repeat: RepeatIcon,
  Users: UsersIcon,
  Webhook: WebhookIcon,
  Calculator: CalculatorIcon,
  Link: LinkIcon
};
export function RecommendationPage() {
  const wizardState = useMemo(() => getWizardState(), []);
  const rec = useMemo(() => getRecommendation(wizardState), [wizardState]);
  const confidenceScore = wizardState.buildingType && wizardState.chargeModel && wizardState.billingManager ? wizardState.stack.frontend ? 96 : 88 : 75;
  const getIcon = (mod: StripeModule) => ICON_MAP[mod.iconName] || ShieldCheckIcon;
  return <div className="min-h-screen bg-pk-surface pb-32" data-id="element-1238">
      <div className="bg-primary/5 border-b border-primary/10 py-3 px-4 text-center" data-id="element-1239">
        <p className="text-sm font-medium text-primary flex items-center justify-center gap-2" data-id="element-1240">
          <ShieldCheckIcon className="w-4 h-4" data-id="element-1241" />
          This preview is free — no payment required
        </p>
      </div>

      <div className="container mx-auto max-w-5xl px-4 py-12" data-id="element-1242">
        {/* Header */}
        <div className="text-center mb-12" data-id="element-1243">
          <div className="inline-flex items-center justify-center p-2 bg-pk-success/10 text-pk-success rounded-full mb-4" data-id="element-1244">
            <ShieldCheckIcon className="w-8 h-8" data-id="element-1245" />
          </div>
          <h1 className="text-4xl font-bold mb-4" data-id="element-1246">
            Your Recommended Payments Stack
          </h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto" data-id="element-1247">
            Based on your answers, here is the optimal Stripe architecture for
            your{' '}
            <span className="font-semibold text-foreground" data-id="element-1248">
              {wizardState.buildingType || 'application'}
            </span>{' '}
            with{' '}
            <span className="font-semibold text-foreground" data-id="element-1249">
              {wizardState.chargeModel?.toLowerCase() || 'standard'}
            </span>{' '}
            pricing.
          </p>
          <div className="inline-flex items-center gap-2 mt-4 px-3 py-1.5 bg-pk-success/10 text-pk-success rounded-full text-sm font-medium" data-id="element-1250">
            <ShieldCheckIcon className="w-4 h-4" data-id="element-1251" />
            {confidenceScore}% confidence match
          </div>
        </div>

        {/* Modules Grid */}
        <div className="mb-12" data-id="element-1252">
          <h2 className="text-2xl font-bold mb-6" data-id="element-1253">
            Recommended Stripe Modules ({rec.modules.length})
          </h2>
          <div className="grid md:grid-cols-2 gap-6" data-id="element-1254">
            {rec.modules.map(mod => {
            const Icon = getIcon(mod);
            return <div key={mod.name} className="bg-card border border-border rounded-xl p-6 shadow-sm" data-id="element-1255">
                  <div className="flex items-start gap-4" data-id="element-1256">
                    <div className="p-3 bg-primary/10 text-primary rounded-lg" data-id="element-1257">
                      <Icon className="w-6 h-6" data-id="element-1258" />
                    </div>
                    <div className="flex-1" data-id="element-1259">
                      <div className="flex items-center gap-2 mb-1" data-id="element-1260">
                        <h3 className="font-semibold text-lg" data-id="element-1261">{mod.name}</h3>
                        {mod.optional && <span className="text-xs bg-muted text-muted-foreground px-2 py-0.5 rounded font-medium" data-id="element-1262">
                            Optional
                          </span>}
                      </div>
                      <p className="text-sm text-muted-foreground mb-3" data-id="element-1263">
                        {mod.description}
                      </p>
                      <div className="text-xs font-medium text-primary bg-primary/10 inline-block px-2 py-1 rounded" data-id="element-1264">
                        Why: {mod.reason}
                      </div>
                    </div>
                  </div>
                </div>;
          })}
          </div>
        </div>

        {/* Webhooks & Data Model */}
        <div className="grid md:grid-cols-2 gap-8 mb-12" data-id="element-1265">
          <div data-id="element-1266">
            <h2 className="text-2xl font-bold mb-6 flex items-center gap-2" data-id="element-1267">
              <WebhookIcon className="w-6 h-6 text-muted-foreground" data-id="element-1268" />
              Required Webhooks ({rec.events.length})
            </h2>
            <div className="bg-card border border-border rounded-xl overflow-hidden shadow-sm" data-id="element-1269">
              <CodeBlock language="events" code={rec.events.join('\n')} data-id="element-1270" />
              <div className="p-4 bg-muted/30 text-sm text-muted-foreground border-t border-border" data-id="element-1271">
                We will automatically configure a single endpoint to listen to
                these {rec.events.length} events.
              </div>
            </div>
          </div>

          <div data-id="element-1272">
            <h2 className="text-2xl font-bold mb-6 flex items-center gap-2" data-id="element-1273">
              <DatabaseIcon className="w-6 h-6 text-muted-foreground" data-id="element-1274" />
              Suggested Data Model ({rec.tables.length} tables)
            </h2>
            <div className="bg-card border border-border rounded-xl p-6 shadow-sm" data-id="element-1275">
              <div className="space-y-4" data-id="element-1276">
                {rec.tables.map(table => <div key={table.name} className="border border-border rounded-lg p-3" data-id="element-1277">
                    <div className="font-mono text-sm font-semibold mb-2 text-primary" data-id="element-1278">
                      {table.name}
                    </div>
                    <div className="text-xs text-muted-foreground flex justify-between" data-id="element-1279">
                      <span data-id="element-1280">{table.columns.slice(0, 3).join(', ')}</span>
                      <span className="font-mono opacity-60" data-id="element-1281">
                        {table.stripeRef}
                      </span>
                    </div>
                  </div>)}
              </div>
            </div>
          </div>
        </div>

        {/* Implementation Pattern */}
        <div className="bg-card border border-border rounded-xl p-6 shadow-sm mb-8" data-id="element-1282">
          <div className="flex items-start gap-4" data-id="element-1283">
            <div className="p-3 bg-primary/10 text-primary rounded-lg" data-id="element-1284">
              <CodeIcon className="w-6 h-6" data-id="element-1285" />
            </div>
            <div className="flex-1" data-id="element-1286">
              <h2 className="text-xl font-bold mb-2" data-id="element-1287">
                Implementation Pattern: {rec.frontendLabel} + {rec.backendLabel}
              </h2>
              <p className="text-muted-foreground text-sm mb-4" data-id="element-1288">
                Your implementation pack will include code snippets tailored for{' '}
                <span className="font-semibold text-foreground" data-id="element-1289">
                  {rec.frontendLabel}
                </span>{' '}
                on the frontend,{' '}
                <span className="font-semibold text-foreground" data-id="element-1290">
                  {rec.backendLabel}
                </span>{' '}
                API routes, and{' '}
                <span className="font-semibold text-foreground" data-id="element-1291">
                  {rec.dbLabel}
                </span>{' '}
                schema migrations.
              </p>
              <div className="flex flex-wrap gap-2" data-id="element-1292">
                {[rec.frontendLabel, rec.backendLabel, rec.dbLabel].map(tech => <span key={tech} className="text-xs font-medium bg-muted px-2.5 py-1 rounded-full" data-id="element-1293">
                      {tech}
                    </span>)}
              </div>
            </div>
          </div>
        </div>

        {/* Rationale */}
        <div className="bg-card border border-border rounded-xl p-8 shadow-sm mb-8" data-id="element-1294">
          <h2 className="text-xl font-bold mb-4" data-id="element-1295">Why we recommend this</h2>
          <ul className="space-y-3 mb-6" data-id="element-1296">
            {rec.rationale.map((r, i) => <li key={i} className="flex items-start gap-2 text-muted-foreground" data-id="element-1297">
                <ShieldCheckIcon className="w-5 h-5 text-pk-success shrink-0 mt-0.5" data-id="element-1298" />
                {r}
              </li>)}
          </ul>

          {rec.warnings.length > 0 && <div className="space-y-3" data-id="element-1299">
              {rec.warnings.map((w, i) => <div key={i} className="bg-pk-amber-light/50 border border-pk-amber/30 rounded-lg p-4 flex gap-3" data-id="element-1300">
                  <AlertTriangleIcon className="w-5 h-5 text-pk-amber shrink-0 mt-0.5" data-id="element-1301" />
                  <div className="text-sm" data-id="element-1302">
                    <span className="font-semibold block mb-1" data-id="element-1303">{w.title}</span>
                    <span className="text-muted-foreground" data-id="element-1304">
                      {w.description}
                    </span>
                  </div>
                </div>)}
            </div>}
        </div>

        {/* Features Summary */}
        {(wizardState.features.trials || wizardState.features.coupons || wizardState.features.tax || wizardState.features.invoices) && <div className="bg-card border border-border rounded-xl p-6 shadow-sm mb-8" data-id="element-1305">
            <h2 className="text-xl font-bold mb-4" data-id="element-1306">Enabled Features</h2>
            <div className="grid sm:grid-cols-2 gap-3" data-id="element-1307">
              {wizardState.features.trials && <div className="flex items-center gap-3 p-3 bg-pk-success/5 rounded-lg border border-pk-success/20" data-id="element-1308">
                  <ShieldCheckIcon className="w-5 h-5 text-pk-success" data-id="element-1309" />
                  <div data-id="element-1310">
                    <div className="font-medium text-sm" data-id="element-1311">Free Trials</div>
                    <div className="text-xs text-muted-foreground" data-id="element-1312">
                      Trial periods on subscription prices
                    </div>
                  </div>
                </div>}
              {wizardState.features.coupons && <div className="flex items-center gap-3 p-3 bg-pk-success/5 rounded-lg border border-pk-success/20" data-id="element-1313">
                  <TicketIcon className="w-5 h-5 text-pk-success" data-id="element-1314" />
                  <div data-id="element-1315">
                    <div className="font-medium text-sm" data-id="element-1316">Coupon Codes</div>
                    <div className="text-xs text-muted-foreground" data-id="element-1317">
                      Promotion codes and discounts
                    </div>
                  </div>
                </div>}
              {wizardState.features.tax && <div className="flex items-center gap-3 p-3 bg-pk-success/5 rounded-lg border border-pk-success/20" data-id="element-1318">
                  <CalculatorIcon className="w-5 h-5 text-pk-success" data-id="element-1319" />
                  <div data-id="element-1320">
                    <div className="font-medium text-sm" data-id="element-1321">Tax Collection</div>
                    <div className="text-xs text-muted-foreground" data-id="element-1322">
                      Automatic tax via Stripe Tax
                    </div>
                  </div>
                </div>}
              {wizardState.features.invoices && <div className="flex items-center gap-3 p-3 bg-pk-success/5 rounded-lg border border-pk-success/20" data-id="element-1323">
                  <ReceiptIcon className="w-5 h-5 text-pk-success" data-id="element-1324" />
                  <div data-id="element-1325">
                    <div className="font-medium text-sm" data-id="element-1326">
                      Invoice Generation
                    </div>
                    <div className="text-xs text-muted-foreground" data-id="element-1327">
                      PDF invoices for every payment
                    </div>
                  </div>
                </div>}
            </div>
          </div>}
      </div>

      {/* Sticky Bottom CTA */}
      <div className="fixed bottom-0 left-0 right-0 bg-background border-t border-border p-4 shadow-[0_-10px_40px_rgba(0,0,0,0.1)] z-50" data-id="element-1328">
        <div className="container mx-auto max-w-5xl flex flex-col sm:flex-row items-center justify-between gap-4" data-id="element-1329">
          <div data-id="element-1330">
            <div className="font-bold text-lg" data-id="element-1331">Ready to build?</div>
            <div className="text-sm text-muted-foreground" data-id="element-1332">
              Provision {rec.modules.length} modules and {rec.events.length}{' '}
              webhook events in your Stripe account.
            </div>
          </div>
          <Link to="/setup/provision" className="w-full sm:w-auto bg-primary text-primary-foreground hover:opacity-90 px-8 py-3 rounded-lg font-medium transition-opacity flex items-center justify-center gap-2 shadow-lg" data-id="element-1333">
            Proceed to provisioning — $29
            <ArrowRightIcon className="w-4 h-4" data-id="element-1334" />
          </Link>
        </div>
      </div>
    </div>;
}